import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// https://github.com/DefinitelyTyped/DefinitelyTyped/blob/90e9b6dfcf8a441ac9271b2b9f00568957d0f5e9/types/axios-concurrency/index.d.ts

type RequestHandler = (req: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig>;
type ResponseHandler = (res: AxiosResponse) => AxiosResponse;
type ResponseErrorHandler = (res: AxiosResponse) => Promise<AxiosResponse>;
interface Task {
  request: InternalAxiosRequestConfig;
  resolver: (value: InternalAxiosRequestConfig) => void;
}

interface ConcurrencyManagerInstance {
  queue: Task[];
  running: Task[];
  shiftInitial: () => void;
  push: (task: Task) => void;
  shift: () => void;
  requestHandler: RequestHandler;
  responseHandler: ResponseHandler;
  responseErrorHandler: ResponseErrorHandler;
  interceptors: {
    request: number | null;
    response: number | null;
  };
  detach: () => void;
}

// https://github.com/bernawil/axios-concurrency/blob/dc7dd91ffda2fc38f80cda35b78b4bc40045e0ba/index.js

const ConcurrencyManager = (axios: AxiosInstance, MAX_CONCURRENT = 5) => {
  if (MAX_CONCURRENT < 1) {
    throw new Error('Concurrency Manager Error: minimum concurrent requests is 1');
  }
  let instance: ConcurrencyManagerInstance = {
    queue: [],
    running: [],
    shiftInitial: () => {
      setTimeout(() => {
        if (instance.running.length < MAX_CONCURRENT) {
          instance.shift();
        }
      }, 0);
    },
    push: (task) => {
      instance.queue.push(task);
      instance.shiftInitial();
    },
    shift: () => {
      if (instance.queue.length) {
        const queued = instance.queue.shift();
        if (queued) {
          queued.resolver(queued.request);
          instance.running.push(queued);
        }
      }
    },
    // Use as interceptor. Queue outgoing requests
    requestHandler: (req) => {
      return new Promise<InternalAxiosRequestConfig>((resolve) => {
        instance.push({ request: req, resolver: resolve });
      });
    },
    // Use as interceptor. Execute queued request upon receiving a response
    responseHandler: (res) => {
      instance.running.shift();
      instance.shift();
      return res;
    },
    responseErrorHandler: (res) => {
      instance.responseHandler(res);
      return Promise.reject(res instanceof Error ? res : new Error('Request failed'));
    },
    interceptors: {
      request: null,
      response: null,
    },
    detach: () => {
      if (instance.interceptors.request) {
        axios.interceptors.request.eject(instance.interceptors.request);
      }
      if (instance.interceptors.response) {
        axios.interceptors.response.eject(instance.interceptors.response);
      }
    },
  };
  // queue concurrent requests
  instance.interceptors.request = axios.interceptors.request.use(
    instance.requestHandler,
  );
  instance.interceptors.response = axios.interceptors.response.use(
    instance.responseHandler,
    instance.responseErrorHandler,
  );
  return instance;
};

export default ConcurrencyManager;
